import {AfterViewInit, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {MatTableDataSource} from "@angular/material/table";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {PaginatorService} from "@shared-services/paginator.service";
import {UtilsService} from "@shared-services/utils.service";
import {PaymentReportLine, TotalTradingReport} from "../trading-report.interface";
import * as Papa from 'papaparse';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {LoadingService} from '@ui-kit/framework/util/service/loading.service';
import {FormControl, FormGroup} from '@angular/forms';

@Component({
  selector: 'app-total-trading-report',
  templateUrl: './total-trading-report.component.html',
  styleUrls: ['./total-trading-report.component.scss']
})
export class TotalTradingReportComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('paginator') paginator!: MatPaginator;
  @ViewChild('sort') sort!: MatSort;
  @Input() totalTradingReport!: TotalTradingReport;

  reportColumns: string[] = ['transactionDate', 'paymentMethod', 'amountReceived'];
  totalReportDatasource = new MatTableDataSource<PaymentReportLine>();
  tableTitle = 'Total Trading Report';
  exportModel = [
    {key:'transactionDate', displayName: 'Transaction Date'},
    {key:'paymentMethod', displayName: 'Payment Method'},
    {key:'amountReceived', displayName: 'Amount Received'}
  ]

  reportFormGroup: FormGroup;

  constructor(public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private paginatorService: PaginatorService,
              public loader: LoadingService,
              ) {
    this.setFilterPredicate();
    this.reportFormGroup = new FormGroup({
      retailerCompanyName: new FormControl(null),
      paymentMethod: new FormControl(null),
    });

    this.reportFormGroup.valueChanges.subscribe(formValues => {
      this.applyFilter(formValues);
    });
  }

  ngOnInit() {
    this.setReportData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['totalTradingReport']) {
      this.setReportData();
    }
  }

  ngAfterViewInit() {
    this.paginator._intl.getRangeLabel = this.paginatorService.getRangeDisplayText;
    this.totalReportDatasource.paginator = this.paginator;
    this.totalReportDatasource.sort = this.sort;
  }

  setReportData() {
    this.totalReportDatasource.data = [ ...this.totalTradingReport.totalReport ];
  }

  setFilterPredicate() {
    this.totalReportDatasource.filterPredicate = (data: PaymentReportLine, filter: string) => {
      const filterValues = JSON.parse(filter);
      const matchFilter = [];

      const paymentMethodMatch = data.paymentMethod.toLowerCase().includes(filterValues.paymentMethod.toLowerCase());
      matchFilter.push(paymentMethodMatch);

      return matchFilter.every(Boolean);
    };
  }

  applyFilter(formValues: any) {
    const filterValues = {
      paymentMethod: formValues.paymentMethod || ''
    };
    this.totalReportDatasource.filter = JSON.stringify(filterValues);

    if (this.totalReportDatasource.paginator) {
      this.totalReportDatasource.paginator.firstPage();
    }
  }

  getAmountReceivedTotal(): number {
    return this.totalReportDatasource.filteredData.map(item => item.amountReceived)
      .reduce((acc, val) => acc + val, 0);
  }

  exportToCsv() {
    const dataWithDisplayLabels = this.totalReportDatasource.filteredData.map(
      (item: any) => {
        const newItem: any = {};
        this.exportModel.forEach((column) => {
          newItem[column.displayName.toUpperCase()] = item[column.key];
        });
        return newItem;
      }
    );

    const csv = Papa.unparse(dataWithDisplayLabels);
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);

    this.downloadFile(url, `${this.tableTitle}-${new Date().toISOString()}.csv`);

  }

  exportToExcel() {

    const dataWithDisplayLabels = this.totalReportDatasource.filteredData.map(
      (item: any) => {
        const newItem: any = {};
        this.exportModel.forEach((column) => {
          newItem[column.displayName.toUpperCase()] = item[column.key];
        });
        return newItem;
      }
    );

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataWithDisplayLabels);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, `${this.tableTitle}-${new Date().toISOString()}.xlsx`);
  }

  exportToPdf() {

    const doc = new jsPDF('landscape', 'pt', 'a4');
    const columns = this.exportModel.map((col) => col.displayName);
    const body = this.totalReportDatasource.filteredData.map((row: any) =>
      this.exportModel.map((col) => row[col.key]?.toString() || '')
    );
    autoTable(doc, {
      head: [columns],
      body: body,
    });
    doc.save(`${this.tableTitle}-${new Date().toISOString()}.pdf`);

  }

  private downloadFile(url: string, fileName: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}
